import { selectAlignedRecordings } from '../selectors/contentMetadataStoreSelectors';
import { ContentMetadataStoreFactoryType } from '../stores/contentMetadataStoreFactory';
import { AlignedRecordings } from '../types/clip-types';
import { isEntitySharedVideo } from './activityUtils';
import { mergeWindows, subtractRanges } from './rangeUtils';

export const filterAlignedRecordingsBySource = (
  recordings: AlignedRecordings[],
  usePureCloudRecording?: boolean
): AlignedRecordings[] => {
  const localRecordingKeys = new Set(recordings.filter(r => r.source === 'local').map(r => r.user.id + r.entity));

  return recordings.filter(recording => {
    if (typeof usePureCloudRecording === 'boolean') {
      if (!usePureCloudRecording && recording.source === 'cloud') {
        return false;
      }
      if (usePureCloudRecording && recording.source === 'local') {
        return false;
      }
    } else if (recording.source === 'cloud' && localRecordingKeys.has(recording.user.id + recording.entity)) {
      return false;
    }

    return true;
  });
};

export const excludeSharedVideoTimeSpans = (recordings: AlignedRecordings[]): AlignedRecordings[] => {
  const sharedVideoRecordings = recordings.filter(recording => isEntitySharedVideo(recording.entity));
  const stageTimeSpansForSharedVideo = sharedVideoRecordings
    .map(
      recording => recording.stage_time_spans?.map(span => [span.start_time, span.end_time] as [number, number]) ?? []
    )
    .flat();
  const mergedSortedTimeSpansForSharedVideo = mergeWindows(stageTimeSpansForSharedVideo);

  return recordings.map(recording => {
    if (isEntitySharedVideo(recording.entity)) {
      return recording;
    }

    const stageTimeSpans = (recording.stage_time_spans?.map(span => [span.start_time, span.end_time]) ?? []) as [
      number,
      number
    ][];
    const mergedAndSortedStageTimeSpans = mergeWindows(stageTimeSpans);
    const adjustedStageTimeSpans = subtractRanges(mergedAndSortedStageTimeSpans, mergedSortedTimeSpansForSharedVideo);
    const finalStageTimeSpans = adjustedStageTimeSpans.map(span => ({ start_time: span[0], end_time: span[1] }));

    return {
      ...recording,
      stage_time_spans: finalStageTimeSpans
    };
  });
};

export const preprocessAlignedRecordings = ({
  recordings,
  usePureCloudRecording
}: {
  recordings: AlignedRecordings[];
  usePureCloudRecording?: boolean;
}): AlignedRecordings[] => {
  const sourceFilteredRecordings = filterAlignedRecordingsBySource(recordings, usePureCloudRecording);
  return excludeSharedVideoTimeSpans(sourceFilteredRecordings);
};
export const getAlignedRecordings = ({
  broadcastId,
  snapshot,
  contentMetadataStoreInstance
}: {
  broadcastId: string | null;
  snapshot?: AlignedRecordings[];
  contentMetadataStoreInstance: ContentMetadataStoreFactoryType;
}) => {
  if (!broadcastId) {
    return [];
  }

  const alignedRecordingsSnapshot =
    snapshot ?? selectAlignedRecordings(broadcastId)(contentMetadataStoreInstance.getState());

  return alignedRecordingsSnapshot ?? [];
};
